import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { TravelExplore as NotFoundIcon } from '@mui/icons-material';
import PageLayout from './PageLayout';
import TWButton from './TWButton';
import { colors, typography } from '../styles/theme';

export type AccessErrorVariant = 'forbidden' | 'not-found';

/** Same messaging for 403 and 404 (unknown route / no access). */
const TITLE = 'Page not found';
const DESCRIPTION =
  "The page you are trying to visit doesn't exist or you don't have the required permission to access it.";
const ICON = (
  <NotFoundIcon sx={{ fontSize: 64, color: colors.primaryLight }} aria-hidden />
);

export interface AccessErrorPageProps {
  /** Kept for routing semantics; UI is identical for both. */
  variant: AccessErrorVariant;
}

/**
 * Full-page state for 403 and 404 — shared Helios-styled messaging.
 */
const AccessErrorPage: React.FC<AccessErrorPageProps> = ({ variant }) => {
  const navigate = useNavigate();

  return (
    <PageLayout maxWidth="sm" contentPaddingTop="48px">
      <Box
        data-testid={`access-error-${variant}`}
        sx={{
          textAlign: 'center',
          py: 4,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>{ICON}</Box>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: typography.fontWeight.bold,
            color: colors.text.primary,
            mb: 2,
          }}
        >
          {TITLE}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: colors.text.secondary,
            maxWidth: 440,
            mx: 'auto',
            mb: 4,
            lineHeight: typography.lineHeight.relaxed,
          }}
        >
          {DESCRIPTION}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <TWButton label="Back to home" onClick={() => navigate('/', { replace: true })} />
        </Box>
      </Box>
    </PageLayout>
  );
};

export default AccessErrorPage;
